import { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Wrench, CheckCircle2, AlertTriangle, Plus } from "lucide-react";
import { PhotoField } from "@/components/CarnetRoute";

const statutOf = (prochain, km) => {
  const diff = prochain - km;
  if (diff <= 0) return { key: "retard", label: "En retard", cls: "bg-red-100 text-red-700 border-red-200" };
  if (diff <= 1000) return { key: "bientot", label: "Bientôt", cls: "bg-orange-100 text-orange-700 border-orange-200" };
  return { key: "ok", label: "OK", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
};

export const PlanEntretien = ({ vehicule, onChange }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const km = vehicule.kilometrage || 0;
  const plan = [...(vehicule.plan_entretien || [])].sort((a, b) => a.prochain_km - b.prochain_km);

  const [doneDialog, setDoneDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [doneForm, setDoneForm] = useState({});
  const [addForm, setAddForm] = useState({ label: "", interval_km: 10000, prochain_km: km + 10000, produit: "" });

  const nbRetard = plan.filter((p) => statutOf(p.prochain_km, km).key === "retard").length;
  const nbBientot = plan.filter((p) => statutOf(p.prochain_km, km).key === "bientot").length;

  const seed = async () => {
    try { await axios.post(`${API}/vehicules/${vehicule.id}/plan/seed`, {}, { headers }); toast.success("Planning initialisé"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openDone = (item) => {
    setCurrent(item);
    setDoneForm({ date: new Date().toISOString().slice(0, 10), km: km, garage: "", prix: "", notes: "", photo: "", produits: item.produit || "" });
    setDoneDialog(true);
  };

  const submitDone = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/vehicules/${vehicule.id}/plan/${current.id}/done`, {
        ...doneForm, km: parseInt(doneForm.km) || km, prix: doneForm.prix === "" ? null : parseFloat(doneForm.prix),
      }, { headers });
      toast.success(`« ${current.label} » enregistré — prochaine échéance recalculée`);
      setDoneDialog(false);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const submitAdd = async () => {
    if (!addForm.label.trim()) { toast.error("Libellé requis"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/vehicules/${vehicule.id}/plan`, {
        ...addForm, interval_km: parseInt(addForm.interval_km) || 10000, prochain_km: parseInt(addForm.prochain_km) || km,
      }, { headers });
      toast.success("Échéance ajoutée");
      setAddDialog(false);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Supprimer cette échéance ?")) return;
    try { await axios.delete(`${API}/vehicules/${vehicule.id}/plan/${id}`, { headers }); toast.success("Supprimé"); onChange(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <Card data-testid="plan-entretien-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2"><Wrench className="w-5 h-5 text-indigo-600" />Planning entretien</CardTitle>
        {plan.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => { setAddForm({ label: "", interval_km: 10000, prochain_km: km + 10000, produit: "" }); setAddDialog(true); }} data-testid="add-plan-btn"><Plus className="w-4 h-4 mr-1" />Échéance</Button>
        )}
      </CardHeader>
      <CardContent>
        {plan.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-slate-500 text-sm mb-3">Aucun planning d'entretien.</p>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={seed} data-testid="seed-plan-btn"><Wrench className="w-4 h-4 mr-2" />Initialiser le planning</Button>
          </div>
        ) : (
          <>
            {(nbRetard > 0 || nbBientot > 0) && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="plan-alerts">
                {nbRetard > 0 && <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200"><AlertTriangle className="w-4 h-4" />{nbRetard} entretien(s) en retard</div>}
                {nbBientot > 0 && <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-orange-50 text-orange-700 border border-orange-200"><AlertTriangle className="w-4 h-4" />{nbBientot} bientôt (&lt; 1000 km)</div>}
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Km prévu</TableHead><TableHead>Entretien</TableHead><TableHead>Produit conseillé</TableHead>
                    <TableHead>Statut</TableHead><TableHead>Dernier fait</TableHead><TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.map((p) => {
                    const st = statutOf(p.prochain_km, km);
                    return (
                      <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                        <TableCell className="font-medium">{p.prochain_km?.toLocaleString()} km<div className="text-xs text-slate-400">tous les {p.interval_km?.toLocaleString()} km</div></TableCell>
                        <TableCell>{p.label}</TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-[200px]">{p.produit || "—"}</TableCell>
                        <TableCell><Badge variant="outline" className={st.cls} data-testid={`plan-statut-${p.id}`}>{st.label}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-500">{p.last_done_km ? `${p.last_done_km.toLocaleString()} km` : "—"}<div>{p.last_done_date ? p.last_done_date.slice(0, 10) : ""}</div></TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 h-8" onClick={() => openDone(p)} data-testid={`plan-done-${p.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Fait</Button>
                          <Button size="sm" variant="ghost" className="text-red-400 h-8 w-8 p-0 ml-1" onClick={() => removeItem(p.id)} data-testid={`plan-del-${p.id}`}>×</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>

      {/* Dialog Marquer comme fait */}
      <Dialog open={doneDialog} onOpenChange={setDoneDialog}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="plan-done-dialog">
          <DialogHeader><DialogTitle>Marquer comme fait — {current?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={doneForm.date || ""} onChange={(e) => setDoneForm({ ...doneForm, date: e.target.value })} data-testid="done-date" /></div>
              <div><Label>Kilométrage</Label><Input type="number" value={doneForm.km} onChange={(e) => setDoneForm({ ...doneForm, km: e.target.value })} data-testid="done-km" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Garage / personne</Label><Input value={doneForm.garage || ""} onChange={(e) => setDoneForm({ ...doneForm, garage: e.target.value })} data-testid="done-garage" /></div>
              <div><Label>Prix (€)</Label><Input type="number" step="0.01" value={doneForm.prix} onChange={(e) => setDoneForm({ ...doneForm, prix: e.target.value })} data-testid="done-prix" /></div>
            </div>
            <div><Label>Produits utilisés</Label><Input value={doneForm.produits || ""} onChange={(e) => setDoneForm({ ...doneForm, produits: e.target.value })} data-testid="done-produits" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={doneForm.notes || ""} onChange={(e) => setDoneForm({ ...doneForm, notes: e.target.value })} /></div>
            <PhotoField label="Photo facture" fileId={doneForm.photo} onUploaded={(id) => setDoneForm({ ...doneForm, photo: id })} testid="done-photo" />
            <p className="text-xs text-indigo-600">Prochaine échéance : {((parseInt(doneForm.km) || km) + (current?.interval_km || 0)).toLocaleString()} km</p>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={submitDone} disabled={saving} data-testid="submit-done"><CheckCircle2 className="w-4 h-4 mr-2" />{saving ? "…" : "Enregistrer"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Ajouter échéance */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md" data-testid="plan-add-dialog">
          <DialogHeader><DialogTitle>Nouvelle échéance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Libellé</Label><Input value={addForm.label} onChange={(e) => setAddForm({ ...addForm, label: e.target.value })} data-testid="add-plan-label" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Intervalle (km)</Label><Input type="number" value={addForm.interval_km} onChange={(e) => setAddForm({ ...addForm, interval_km: e.target.value })} data-testid="add-plan-interval" /></div>
              <div><Label>Prochaine (km)</Label><Input type="number" value={addForm.prochain_km} onChange={(e) => setAddForm({ ...addForm, prochain_km: e.target.value })} data-testid="add-plan-prochain" /></div>
            </div>
            <div><Label>Produit conseillé</Label><Input value={addForm.produit} onChange={(e) => setAddForm({ ...addForm, produit: e.target.value })} data-testid="add-plan-produit" /></div>
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={submitAdd} disabled={saving} data-testid="submit-add-plan">Ajouter</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PlanEntretien;
